import React, { useEffect, useRef, useState } from 'react'
import { When } from 'react-if'
import Sticky from 'react-stickynode'
import { useGuildMember } from '@/services/GuildUserService'
import { Project } from '@/services/ProjectsService'
import { ContentSeasonEdge } from '@/types/codegen-federation'
import { Media, processContentEpisodesToMediasWithFiltering } from '@/utils/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'
import { useWatchProject } from '@/views/WatchProjectView/useWatchProject'
import { PillTabItem, PillTabs } from '../PillTabs'
import { EpisodeCard } from './EpisodeCard'

interface WatchEpisodesTabProps {
  projectData: Project
  movieSpecialMedias: Media[]
  slug: string
  isGuildMember: boolean
  isGeoblockedProject: boolean
  hasWatchableReasons: boolean
}

export const WatchEpisodesTab: React.FC<WatchEpisodesTabProps> = ({
  projectData,
  movieSpecialMedias,
  isGuildMember,
  isGeoblockedProject,
  hasWatchableReasons,
}) => {
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const router = useSafeRouter()
  const { guildPermissions } = useGuildMember()
  const [stickyTop, setStickyTop] = useState(86)
  const containerRef = useRef<HTMLDivElement>(null)
  const userHasEarlyAccess = Boolean(guildPermissions.hasEarlyAccess)
  const [activeSeason, setActiveSeason] = React.useState<ContentSeasonEdge | undefined>(undefined)
  const numSeasons = projectData?.title?.seasons?.edges?.length
  const { project } = useWatchProject({ projectSlug: projectData?.slug, skip: numSeasons === 0 })
  const isSeriesOrPodcast = Boolean(projectData?.projectType === 'series' || projectData?.projectType === 'podcast')

  useEffect(() => {
    if (!projectData?.title?.seasons?.edges || projectData?.title?.seasons?.edges.length === 0 || !router.isReady) {
      return
    }

    const seasonNameOrIndex = getItemOrFirstEntry(router.query.season)
    const seasons = projectData?.title?.seasons?.edges?.filter(isDefined) || []

    if (seasonNameOrIndex) {
      // Try to find season by name or number from URL parameter
      const seasonByName = seasons.find((edge) =>
        edge?.node?.name?.toLowerCase().includes(seasonNameOrIndex.toLowerCase()),
      )
      const seasonNumber = parseInt(seasonNameOrIndex)
      const seasonByNumber = !isNaN(seasonNumber)
        ? seasons.find((edge) => edge?.node?.seasonNumber === seasonNumber)
        : undefined

      if (seasonByName) {
        setActiveSeason(seasonByName as ContentSeasonEdge)
      } else if (seasonByNumber) {
        setActiveSeason(seasonByNumber as ContentSeasonEdge)
      } else {
        // If no match found, default to first season
        setActiveSeason(seasons[0] as ContentSeasonEdge)
      }
    } else {
      // No season parameter, default to first season
      setActiveSeason(seasons[0] as ContentSeasonEdge)
    }
  }, [projectData, router.isReady, router.query.season])

  const medias = processContentEpisodesToMediasWithFiltering(
    activeSeason?.node?.episodes?.edges?.filter(isDefined) || [],
    {
      projectName: project?.name,
      projectType: project?.projectType,
      trailerText: t('trailer', 'Trailer'),
      locale,
      selectedSeasonId: activeSeason?.node?.id || '',
      isGuildMember,
    },
    isGeoblockedProject,
    project,
  )

  const watchableMedias = isSeriesOrPodcast ? medias : movieSpecialMedias

  const seasonTabs: PillTabItem[] = React.useMemo(() => {
    const edges = projectData?.title?.seasons?.edges ?? []
    return edges
      .filter(isDefined)
      .map((season) => {
        const id = season?.node?.id ?? ''
        const count = season?.node?.episodes?.edges?.length ?? 0
        return {
          id,
          primary: `${t('season', 'Season')} ${season?.node?.seasonNumber}`,
          secondary: `${count} ${t('episodes', 'Episodes')}`,
        }
      })
      .filter((it) => Boolean(it.id))
  }, [projectData, t])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 1024px)')
    const update = () => setStickyTop(mq.matches ? 112 : 86)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (containerRef.current && typeof window !== 'undefined') {
      const containerTop = containerRef.current.getBoundingClientRect().top + window.scrollY
      const currentScroll = window.scrollY
      const stickyOffset = stickyTop

      // If we're scrolled past the container, scroll back to it
      if (currentScroll > containerTop) {
        window.scrollTo({
          top: containerTop - stickyOffset,
          behavior: 'smooth',
        })
      }
    }
  }, [activeSeason?.node?.id, stickyTop])

  return (
    <div ref={containerRef} className="episodes-tab-container flex h-full w-full flex-col lg:flex-row">
      <When condition={isSeriesOrPodcast}>
        <div className="relative lg:w-full lg:min-w-[396px] lg:max-w-fit xl:min-w-[428px]">
          <Sticky
            activeClass="z-[998] opacity-1 relative active"
            releasedClass="z-0 opacity-0"
            className="opacity-1 relative w-full transition-opacity duration-300 ease-in-out"
            top={stickyTop}
            bottomBoundary=".episodes-tab-container"
          >
            <PillTabs
              items={seasonTabs}
              activeId={activeSeason?.node?.id}
              onChange={(item) => {
                const match = projectData?.title?.seasons?.edges?.find((s) => s?.node?.id === item.id)
                if (match) setActiveSeason(match as ContentSeasonEdge)
              }}
              ariaLabel={t('season_tabs_aria_label', 'Season tabs')}
            />
          </Sticky>
        </div>
      </When>
      <div className="flex min-h-[80px] flex-col gap-y-10 pl-4 pr-3 md:min-h-[150px] md:gap-y-6 md:px-12 lg:min-h-[280px] lg:gap-y-10 lg:pr-16">
        {watchableMedias?.map((media, i) => (
          <div key={media?.guid}>
            <EpisodeCard
              className="relative flex min-h-[100px] w-full shrink-0 snap-center snap-always flex-row items-start gap-4"
              thumbnailClassName="h-full w-full min-w-[180px] md:min-w-[256px] lg:min-w-[300px]"
              {...media}
              itemIndex={i}
              pagePlacement={isSeriesOrPodcast ? 0 : 1}
              imgPriority={i <= 4}
              includeHoverScale={false}
              userHasEarlyAccess={userHasEarlyAccess}
              hasWatchableReasons={hasWatchableReasons}
              projectType={projectData?.projectType}
              theaterEpisodeCard
            />
          </div>
        ))}
      </div>
    </div>
  )
}
